import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./dashboard-mobile.css";
import SignIn from "./components/SignIn";
import AdminDashboard from "./components/AdminDashboard";
import EmployeeDashboard from "./components/EmployeeDashboard";
import Navigation from "./components/Navigation";
import { getAuthToken, getUser } from "./services/api";
import {
  registerServiceWorker,
  setupInstallPrompt,
} from "./services/pushNotifications";

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  adminOnly?: boolean;
  isAuthenticated: boolean;
  user: any;
}> = ({ children, adminOnly = false, isAuthenticated, user }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkAuth();
    // Register service worker once at app level
    registerServiceWorker();
    setupInstallPrompt();

    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, []);

  const checkAuth = () => {
    const token = getAuthToken();
    const userData = getUser();
    setIsAuthenticated(!!token);
    setUser(userData);
  };

  return (
    <Router>
      <div className="App">
        {isAuthenticated && <Navigation onLogout={checkAuth} />}
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <SignIn onLoginSuccess={checkAuth} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
                {user?.role === "admin" ? (
                  <AdminDashboard />
                ) : (
                  <EmployeeDashboard />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                user={user}
                adminOnly
              >
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <Navigate
                to={isAuthenticated ? "/dashboard" : "/login"}
                replace
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
